import React, { useContext, useEffect, useState } from 'react';
import { bool, oneOf } from 'prop-types';
import { Row, Col } from '@thd-olt-component-react/grid';
import classNames from 'classnames';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import { AisleBayPod } from '@thd-olt-component-react/aisle-bay';
import { ProductPodContext } from '../../ProductPodContext';
import { ProductBadge } from '../product-badge/ProductBadge';
import { ProductFavorites } from '../product-favorites/ProductFavorites';
import { ProductHeader } from '../product-header/ProductHeader';
import { ProductRating } from '../product-rating/ProductRating';
import { ProductImage } from '../product-image/ProductImage';
import { ProductServiceAddOns } from '../product-service-add-ons/ProductServiceAddOns';
import { ProductPrice } from '../product-price/ProductPrice';
import { ProductShipping } from '../product-shipping/ProductShipping';
import { ProductStore } from '../product-store/ProductStore';
import { ProductATC } from '../product-atc/ProductATC';
import { ProductCustomOptions } from '../product-custom-options/ProductCustomOptions';
import { ProductSponsored } from '../product-sponsored/ProductSponsored';
import { ProductCompare } from '../product-compare/ProductCompare';
import { ProductMobileDpd } from './ProductMobileDpd';
import { ProductSeeSimilarItems } from '../product-see-similar-items/ProductSeeSimilarItems';
import { ProductModel } from '../product-model/ProductModel';
import {
  isCustomKitchenCabinetProduct,
  isSellableAtSelectedStore,
} from '../../utils';
import './ProductMobile.style.scss';

export const ProductMobileSingleColumn = ({ isEtchOne, showDpdPod, collection }) => {
  const {
    comparable,
    product,
    position,
    onFavoriteChange,
    buyMoreSaveMore,
    isHovered,
    previousItemId,
    itemId,
    storeId,
    onChangeSuperSku,
    onHoverSuperSku,
    target,
    canonicalUrl,
    showAisleAndBay,
    showKPF,
    showSimilarItemButton,
    showSimilarLinkInstock
  } = useContext(ProductPodContext);

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  if (showDpdPod) return <ProductMobileDpd isEtchOne={isEtchOne} />;

  const { details, info, badges, identifiers } = product || {};
  const { storeSkuNumber } = identifiers || {};
  const {
    collection: collectionObj
  } = details || {};
  const {
    name: collectionName,
    url: collectionUrl
  } = collectionObj || {};

  const superSkuItemId = isHovered ? previousItemId : itemId;
  const showShopThisCollectionLink = collectionName && collectionUrl && collection === 'below';
  const productPodRightSideColWrapper = classNames('product-pod-right-side-col-wrapper', {
    'product-pod-right-side-col-wrapper--brio': info?.isBrioSku
  });
  const productPodLeftSideColWrapper = classNames('product-pod__left-side', {
    'product-pod__left-side--brio': info?.isBrioSku
  });

  const showAisleBayForProduct = showAisleAndBay && storeSkuNumber && isSellableAtSelectedStore({ product });

  const showATCButtonAtTheBottom = isCustomKitchenCabinetProduct(product)
    && typeof window !== 'undefined' && window?.outerWidth === 320;

  return (
    <>
      <ProductFavorites
        className="product-pod__inline__favorite"
        onFavoriteChange={onFavoriteChange}
        position="topRight"
        showCircle
      />
      <div className="product-pod__badges">
        <ProductBadge badges={badges} />
      </div>
      <div className="pod-header-wrapper">
        <ProductSponsored position={position} />
        <ProductHeader brand="inline" disableShopThisCollection />
        {info?.isBrioSku && (
          <div className="ratings-and-model-container-brio">
            <ProductRating />
            <ProductModel />
          </div>
        )}
      </div>
      <Row>
        <div className="pod-mid-elements-wrapper">
          <Col fallback="6" className={productPodLeftSideColWrapper}>
            <ProductImage buyMoreSaveMore={buyMoreSaveMore} dualImageSwipe />
            {mounted && (
              <SuperSkuMiniSwatches
                itemId={superSkuItemId}
                canonicalUrl={canonicalUrl}
                storeId={storeId}
                onChange={onChangeSuperSku}
                onHover={onHoverSuperSku}
                center
              />
            )}
            <ProductCustomOptions />
          </Col>
          <Col className={productPodRightSideColWrapper} fallback="6">
            {showShopThisCollectionLink && (
              <a className="product-pod__title__collection" href={collectionUrl} target={target}>
                Shop this Collection
              </a>
            )}
            {!info?.isBrioSku && <ProductRating />}
            <ProductServiceAddOns />
            <ProductPrice large={false} className="product-pod__pricing" />
            {showKPF && (
              <KeyProductFeatures
                hideImage
                storeId={storeId}
                itemId={itemId}
                maxfeatures={4}
                simple
                oneColumnGrid
                className="product-pod__mw-kpf"
              />
            )}
            {!info?.isBrioSku && <ProductStore className="no-margin" checkGeneric />}
            {!info?.isBrioSku && <ProductShipping className="product-pod__fulfillment--shipping" />}
            {showAisleBayForProduct && <AisleBayPod storeId={storeId} storeSkuNumber={storeSkuNumber} />}
            {showSimilarItemButton && !showATCButtonAtTheBottom && <ProductSeeSimilarItems />}
            {!showATCButtonAtTheBottom && <ProductATC className="product-pod__add-to-cart" outline checkGeneric />}
            {showSimilarLinkInstock && (
              <div className="product-pod__similar-instock--link"><ProductSeeSimilarItems showSimilarInStock /></div>
            )}
          </Col>
          {showATCButtonAtTheBottom && <ProductATC className="product-pod__add-to-cart" outline checkGeneric />}
        </div>
      </Row>
      {comparable
      && (
        <Row className="product-pod__compare-row">
          <Col fallback="12" flatten>
            <ProductCompare />
          </Col>
        </Row>
      )}
    </>

  );
};

ProductMobileSingleColumn.propTypes = {
  collection: oneOf(['below', 'hidden']),
  isEtchOne: bool,
  showDpdPod: bool
};

ProductMobileSingleColumn.defaultProps = {
  collection: 'below',
  isEtchOne: false,
  showDpdPod: false
};

ProductMobileSingleColumn.displayName = 'ProductMobileSingleColumn';
